import React from 'react';
import { shallowEqual } from 'react-redux';
import { Box } from '@apia/theme';
import { useAppSelector } from '../../../../../hooks/hooks';
import useConditionalWhyDidYouUpdate from '../../../../../hooks/useConditionalWhyDidYouUpdate';
import useContext from '../../../../../hooks/useContext';
import { makeCellFieldId } from '../../../../../store/dataGridSlice/util';
import usePropsSelector from '../../../../../store/propsStore/usePropsSelector';
import useDomProps from '../../../../../store/selection/useDomProps';
import { TApiaComponent } from '../../../../../types';
import { noNaN } from '../../../../../utils/util';
import ApiaComponent from '../../ApiaComponent';
import { DataGridIdentifier, DataGridRowIdentifierContext } from './context';

interface ICell {
  columnIndex: number /* 
  id: string;
  rowId: string; */;
}

const Cell = ({ columnIndex, ...props }: ICell) => {
  const { gridId } = useContext(DataGridIdentifier);
  const { rowIndex } = useContext(DataGridRowIdentifierContext);
  const cellId = React.useMemo(
    () => makeCellFieldId(gridId, columnIndex, rowIndex),
    [columnIndex, gridId, rowIndex],
  );
  const { startIndex, isColumnRequired } = useAppSelector((global) => {
    const state = global.dataGridSlice[gridId];
    const grid = state.context.grid.field;
    return {
      startIndex: state.context.grid.field.startIndex,
      isColumnRequired: state.requiredColumns.includes(columnIndex),
      grid: grid,
    };
  }, shallowEqual);

  const cellProps = usePropsSelector(cellId);
  const isHidden = cellProps?.fieldType === 'hidden';
  const columnWidth = cellProps?.colWidth;

  const isStoreHidden = useAppSelector(
    (global) => global.dataGridSlice[gridId].columns[columnIndex].isHidden,
  );

  const domProps = useDomProps(
    'td',
    { columnIndex, rowIndex },
    (global) => global.dataGridSlice[gridId],
  );
  const minWidth = React.useMemo(
    () => (cellProps?.fieldType === 'check' ? 100 : 250),
    [cellProps?.fieldType],
  );

  useConditionalWhyDidYouUpdate(
    cellId,
    {
      cellProps,
      domProps,
      gridId,
      rowIndex,
      cellId,
      startIndex,
      ...props,
    },
    false,
  );

  const sx = React.useMemo(
    () => ({
      maxWidth: `${columnWidth ? Number(columnWidth) : minWidth}px`,
      width: `${columnWidth ? Number(columnWidth) : minWidth}px`,
      display:
        cellProps?.visibilityHidden || isStoreHidden ? 'none' : undefined,

      '& > *': {
        maxWidth: `${columnWidth ? Number(columnWidth) : minWidth}px`,
      },
      pt:
        cellProps.required && !isColumnRequired && cellProps.attId ? '20px' : 0,
    }),
    [
      cellProps.attId,
      cellProps.required,
      cellProps?.visibilityHidden,
      columnWidth,
      isColumnRequired,
      isStoreHidden,
      minWidth,
    ],
  );
  const visibilitySx = React.useMemo(
    () => (isHidden ? { display: 'none !important' } : {}),
    [isHidden],
  );

  const isValid = !cellProps.message;

  return (
    //el sx esta mal definido, no se puede usar sx en un td por ende nunca aplico esos estilos, cuando se usa un style los estilos rompen el orden de las cells quedando en la posicion siguente
    <td
      {...domProps}
      className="grid__cell"
      sx={{ ...sx, ...visibilitySx }}
      style={{
        paddingTop:
          cellProps.required && !isColumnRequired && cellProps.attId
            ? '20px'
            : '4px',
      }}
    >
      {window.SHOW_REQUIRED_POSITION !== '0' &&
        cellProps.required &&
        !isColumnRequired &&
        cellProps.attId && (
          <Box
            as="span"
            sx={{
              flexShrink: '0',
              color: 'danger',
              position: 'absolute',
              top: '0',
              left: '2',
              fontSize: 'large!important',
              width: 'fit-content',
              pointerEvents: 'none',
              fontWeight: 'bold',
              zIndex: 1,
            }}
          >
            *
          </Box>
        )}
      <Box className="grid__cell__content">
        {cellProps?.fieldType && (
          <Box
            className={`fieldWrapper ${isValid ? '' : 'invalid'} ${
              cellProps.cssClass ?? ''
            }`}
          >
            <ApiaComponent
              field={cellProps as unknown as TApiaComponent}
              onGrid
              gridFieldId={cellId}
              index={rowIndex + noNaN(startIndex)}
              gridId={gridId}
              gridColumn={columnIndex}
            />
          </Box>
        )}
      </Box>
      {window.SHOW_REQUIRED_POSITION === '0' &&
        cellProps.required &&
        !isColumnRequired &&
        cellProps.attId && (
          <Box
            as="span"
            sx={{
              flexShrink: '0',
              color: 'danger',
              position: 'absolute',
              top: '0',
              right: '2',
              fontSize: 'large!important',
              width: 'fit-content',
              pointerEvents: 'none',
              fontWeight: 'bold',
              zIndex: 1,
            }}
          >
            *
          </Box>
        )}
    </td>
  );
};

export default Cell;
